import { useAuth, useEventMemberships } from '@shared/components/AuthProvider';
import type { EventMembership } from '@shared/components/AuthProvider';
import { useNativeState } from '@shared/core/native';
import { useEffect, useRef } from 'react';
import qs from '@shared/core/queryString';
import { useHistory } from '@react-router';
import { withWindow } from '@shared/utils/withWindow';
import { EventUserStatus } from '@graphql/generated-base';

/**
 * Mapping of scenario query param to redirect subpath
 */
const SCENARIO_MAPPING: Record<string, string> = {
  'save-the-date': '/edit/cards/wedding/save-the-date',
  invitation: '/edit/cards/wedding/invitation',
};

type ValidEventMembership = {
  id: string;
  event: NonNullable<EventMembership['event']>;
  status: EventUserStatus.admin | EventUserStatus.owner;
};

const isValidEventMembership = (eventMembership: EventMembership): eventMembership is ValidEventMembership => {
  const isOwnerOrAdmin = eventMembership.status === EventUserStatus.admin || eventMembership.status === EventUserStatus.owner;
  const hasEventWebsite = !!eventMembership.event?.website;
  return isOwnerOrAdmin && hasEventWebsite;
};

/**
 * Auto-redirect hook for the `/createwedding` form route.
 *
 * Two redirect paths:
 *
 * 1. **Scenario redirect** — when a valid `?scenario=` query param is present and the
 *    user is logged in with events, redirect to the appropriate scenario page (or event
 *    picker if 2+ events).
 *
 * 2. **Existing-events redirect (JW-53)** — when a logged-in user with 1+ events lands
 *    on `/createwedding` without `?new=true`, redirect to `/account/events` so they can
 *    manage existing events or explicitly choose to create another one. The `?new=true`
 *    escape hatch is used by the "Create Event" button on `/account/events` to prevent
 *    a redirect loop.
 *
 * When the user is logged out (97%+ of traffic) or logged in with 0 events, no redirect
 * fires and the create-event form renders normally.
 *
 * **Native webview bypass:** When the page is loaded inside the Joy native app webview
 * (`isNative`), all redirects are skipped. The native app intentionally navigates to
 * `/createwedding` to enter the create flow, so bouncing to `/account/events` would
 * break the in-app experience.
 */
export const useAutoRedirectToScenario = () => {
  const { isNative } = useNativeState();
  const { isLoggedIn, isCurrentUserSettled } = useAuth();
  const scenarioRef = useRef(qs.getValueString('scenario'));
  const hasValidScenario = !!scenarioRef.current && Object.prototype.hasOwnProperty.call(SCENARIO_MAPPING, scenarioRef.current);
  const isNewEventIntent = qs.getValueString('new') === 'true';
  const { eventMemberships, loading: isLoadingEventMemberships } = useEventMemberships(undefined, { skip: !isLoggedIn || isNative });
  const history = useHistory();
  const shouldStopConsideringRedirectRef = useRef(false);

  useEffect(() => {
    // Native app webviews intentionally enter the create flow through /createwedding.
    // Do not redirect them to /account/events or scenario destinations.
    if (isNative) {
      shouldStopConsideringRedirectRef.current = true;
      return;
    }

    // Wait for auth to settle before making any redirect decisions.
    // Without this gate, isLoggedIn starts as false while Auth0 resolves,
    // which would permanently set shouldStopConsideringRedirectRef and
    // prevent the redirect from ever firing once auth settles.
    if (!isCurrentUserSettled || isLoadingEventMemberships || shouldStopConsideringRedirectRef.current) {
      return;
    }

    const eventsAsOwnerOrAdmin = eventMemberships.filter(isValidEventMembership);
    const hasOneOrMoreEvents = eventsAsOwnerOrAdmin.length > 0;

    if (!isLoggedIn || !hasOneOrMoreEvents) {
      // Not logged in or no events — show the create-event form, stop checking.
      shouldStopConsideringRedirectRef.current = true;
      return;
    }

    // Scenario redirect (existing behavior): logged-in user with events + ?scenario param
    const redirectSubPath = scenarioRef.current && SCENARIO_MAPPING[scenarioRef.current];
    if (hasValidScenario && redirectSubPath) {
      const basePath = eventsAsOwnerOrAdmin.length === 1 ? `/${eventsAsOwnerOrAdmin[0].event.website}` : '/event';
      // Full document navigation so SSR injects the admin/editor CSS chunks.
      withWindow(global => global.location.assign(basePath + redirectSubPath + history.location.search));
      return;
    }

    // JW-53: Logged-in user with 1+ events, no scenario — redirect to /account/events
    // unless they arrived with ?new=true (explicit intent to create another event).
    if (!isNewEventIntent) {
      // Full document navigation so SSR injects the AccountMe CSS chunks.
      withWindow(global => global.location.assign('/account/events'));
      return;
    }

    // User has ?new=true — they explicitly want to create. Let them through.
    shouldStopConsideringRedirectRef.current = true;
  }, [isNative, isCurrentUserSettled, hasValidScenario, isLoadingEventMemberships, isLoggedIn, history, eventMemberships, isNewEventIntent]);
};
